import { CartesianGrid, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { priceBounds, stepPoints, windowStart, type PriceSeries } from '@/lib/product-intelligence';
import { vendorColor } from './vendor-colors';

const tickDate = (t: number) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const tickPrice = (p: number) => `$${p.toFixed(p >= 100 ? 0 : 2)}`;

/**
 * Multi-vendor price trend over the selected window, drawn as STEP lines: a
 * vendor's price holds and then jumps, and a sloped line would invent prices
 * nobody ever quoted. Each line starts at the window's left edge on its
 * opening price and carries today's price to the right edge.
 *
 * The x-axis spans the window itself, not the data, so switching windows keeps
 * a stable scale. The cheapest vendor (slot 0) is drawn last, thicker and with
 * a halo on its end point, so "who should I buy from" reads without the legend.
 */
export function VendorTrendChart({ series, now, height = 200 }: { series: PriceSeries; now: Date; height?: number }) {
  const bounds = priceBounds(series);
  if (!bounds) return null;
  const from = windowStart(series.window, now).getTime();
  const to = now.getTime();
  // Four evenly spaced price guides across the padded range. Recharts only
  // "nices" an auto domain, so with a fixed one its own ticks land unevenly.
  const priceTicks = [0, 1, 2, 3].map((i) => bounds.min + ((bounds.max - bounds.min) * i) / 3);

  // Dearest first, so the cheapest line ends up on top.
  const drawOrder = series.vendors.map((v, i) => ({ v, i })).reverse();

  return (
    <div
      style={{ height }}
      className="w-full"
      role="img"
      aria-label={`Vendor price trend over the ${series.window.fullName}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart margin={{ top: 10, right: 14, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis
            type="number"
            dataKey="t"
            domain={[from, to]}
            tickFormatter={tickDate}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            tickCount={5}
            allowDuplicatedCategory={false}
          />
          <YAxis
            type="number"
            domain={[bounds.min, bounds.max]}
            tickFormatter={tickPrice}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            width={56}
            ticks={priceTicks}
          />
          {drawOrder.map(({ v, i }) => {
            const data = stepPoints(v, series.window, now);
            const color = vendorColor(i);
            const cheapest = i === 0;
            return (
              <Line
                key={v.payeeId}
                data={data}
                dataKey="price"
                name={v.payeeName}
                type="stepAfter"
                stroke={color}
                strokeWidth={cheapest ? 2.6 : 1.8}
                isAnimationActive={false}
                activeDot={false}
                dot={(props: any) => {
                  const { cx, cy, index } = props;
                  if (index !== data.length - 1 || cx == null || cy == null) {
                    return <g key={`${v.payeeId}-${index}`} />;
                  }
                  return (
                    <g key={`${v.payeeId}-end`}>
                      {cheapest && <circle cx={cx} cy={cy} r={8} fill={color} fillOpacity={0.18} />}
                      <circle cx={cx} cy={cy} r={cheapest ? 4.5 : 3.2} fill={color} />
                    </g>
                  );
                }}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
